import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Toaster, toast } from "react-hot-toast";
import { apiCall } from "utils/apiClient";
import Pagination from "components/pagination";

const TAB_SERVICE = "service";

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const normalizeFaq = (faq) => {
    const serviceId =
        faq?.service_id ??
        faq?.serviceId ??
        faq?.serviceID ??
        faq?.service?.id ??
        "";
    const categoryId =
        faq?.category_id ??
        faq?.categoryId ??
        faq?.categoryID ??
        faq?.category?.id ??
        "";

    return {
        ...faq,
        service_id: serviceId,
        category_id: categoryId,
        question:
            faq?.question ??
            faq?.faq_question ??
            faq?.title ??
            faq?.faq_title ??
            "",
        answer:
            faq?.answer ??
            faq?.faq_answer ??
            faq?.description ??
            "",
        category_name:
            faq?.category_name ??
            faq?.categoryName ??
            faq?.category?.name ??
            "",
    };
};

const isObject = (value) => value && typeof value === "object";

const looksLikeFaq = (item) => {
    if (!isObject(item)) return false;
    return (
        "question" in item ||
        "faq_question" in item ||
        "faqQuestion" in item ||
        "answer" in item ||
        "faq_answer" in item ||
        "faqAnswer" in item
    );
};

const findArrayInPayload = (payload, predicate) => {
    if (Array.isArray(payload)) return payload;
    if (!isObject(payload)) return [];

    const values = Object.values(payload);
    const arrays = values.filter(Array.isArray);

    if (predicate) {
        const match = arrays.find((arr) => arr.some(predicate));
        if (match) return match;
    }

    for (const value of values) {
        if (isObject(value) && !Array.isArray(value)) {
            const found = findArrayInPayload(value, predicate);
            if (found.length) return found;
        }
    }

    return arrays[0] || [];
};

const getArrayData = (payload, predicate) =>
    findArrayInPayload(payload, predicate);

const isSuccessResponse = (result) =>
    !!result &&
    (result.status === 200 ||
        result.status === 201 ||
        result.status === 204 ||
        result.success === true);

const getEmptyForm = (serviceId = "") => ({
    id: null,
    category_id: "",
    service_id: serviceId,
    question: "",
    answer: "",
    status: 1,
});

const normalizeService = (service) => ({
    ...service,
    id:
        service?.id ??
        service?.service_id ??
        service?.serviceId ??
        "",
    name:
        service?.name ??
        service?.service_name ??
        service?.serviceName ??
        "",
    faq_count: Number(
        service?.faq_count ??
        service?.faqs_count ??
        service?.faqCount ??
        service?.count ??
        0
    ) || 0,
});

const Faqs = () => {
    const navigate = useNavigate();
    const [faqs, setFaqs] = useState([]);
    const [services, setServices] = useState([]);
    const [activeTab, setActiveTab] = useState(TAB_SERVICE);
    const [form, setForm] = useState(getEmptyForm());
    const [activeServiceId, setActiveServiceId] = useState("");
    const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
    const [modalView, setModalView] = useState("list");
    const [servicesPage, setServicesPage] = useState(1);
    const [servicesPerPage, setServicesPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        [TAB_SERVICE]: { page: 1, perPage: 10 },
    });

    const callDeleteWithFallback = async (endpoints) => {
        for (const endpoint of endpoints) {
            try {
                const result = await apiCall({
                    endpoint,
                    method: "DELETE",
                });
                if (isSuccessResponse(result)) {
                    return true;
                }
            } catch (error) {
                console.error(`Delete failed for endpoint: ${endpoint}`, error);
            }
        }
        return false;
    };

    const fetchServices = async () => {
        const data = await apiCall({
            endpoint: "faqs/services-with-count",
            method: "GET"
        });
        if (isSuccessResponse(data)) {
            const raw = getArrayData(data.data ?? data);
            setServices(raw.map(normalizeService).filter((service) => hasValue(service.id)));
        } else {
            setServices([]);
        }
    };

    const fetchFaqsByService = async (serviceId) => {
        if (!serviceId) {
            setFaqs([]);
            return;
        }

        const data = await apiCall({
            endpoint: `faqs/service/${serviceId}`,
            method: "GET",
        });

        if (isSuccessResponse(data)) {
            const raw = getArrayData(data.data ?? data, looksLikeFaq);
            setFaqs(raw.map(normalizeFaq));
            return;
        }

        setFaqs([]);
    };

    useEffect(() => {
        fetchServices();
    }, []);

    const activeService = useMemo(
        () => services.find((service) => String(service.id) === String(activeServiceId)),
        [services, activeServiceId]
    );

    const activeFaqs = faqs;
    const activePagination = pagination[activeTab];
    const totalRecords = activeFaqs.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / activePagination.perPage));

    useEffect(() => {
        if (activePagination.page > totalPages) {
            setPagination((prev) => ({
                ...prev,
                [activeTab]: { ...prev[activeTab], page: totalPages },
            }));
        }
    }, [activePagination.page, activeTab, totalPages]);

    const indexOfLastRecord = activePagination.page * activePagination.perPage;
    const indexOfFirstRecord = indexOfLastRecord - activePagination.perPage;
    const currentFaqs = activeFaqs.slice(indexOfFirstRecord, indexOfLastRecord);
    const displayedFaqsCount = Math.min(totalRecords, indexOfFirstRecord + currentFaqs.length);
    const totalServiceRecords = services.length;
    const totalServicePages = Math.max(1, Math.ceil(totalServiceRecords / servicesPerPage));
    const servicesLastIndex = servicesPage * servicesPerPage;
    const servicesFirstIndex = servicesLastIndex - servicesPerPage;
    const currentServices = services.slice(servicesFirstIndex, servicesLastIndex);
    const displayedServicesCount = Math.min(totalServiceRecords, servicesFirstIndex + currentServices.length);

    useEffect(() => {
        if (servicesPage > totalServicePages) {
            setServicesPage(totalServicePages);
        }
    }, [servicesPage, totalServicePages]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            ...form,
            category_id: "",
            service_id: form.service_id || activeServiceId || "",
        };

        if (!payload.service_id) {
            toast.error("Select service first");
            return;
        }

        const method = form.id ? "PUT" : "POST";
        const endpoint = form.id ? `/faqs/${form.id}` : "faqs";

        const result = await apiCall({
            endpoint,
            method,
            data: payload
        });

        if (result && (result.status === 200 || result.status === 201)) {
            toast.success(`FAQ ${form.id ? "updated" : "added"} successfully`);
            setActiveServiceId(String(payload.service_id));
            setForm(getEmptyForm(payload.service_id));
            setModalView("list");
            fetchFaqsByService(payload.service_id);
            fetchServices();
        } else {
            toast.error("Operation failed");
        }
    };

    const handleEdit = (faq) => {
        const nextId = String(faq.service_id || activeServiceId || "");
        setActiveTab(TAB_SERVICE);
        setActiveServiceId(nextId);
        setIsFaqModalOpen(true);
        setModalView("form");
        setForm({
            id: faq.id,
            category_id: "",
            service_id: nextId,
            question: faq.question,
            answer: faq.answer,
            status: faq.status ?? 1,
        });
    };

    const handleAdd = () => {
        setActiveTab(TAB_SERVICE);
        setForm(getEmptyForm(activeServiceId));
        setIsFaqModalOpen(true);
        setModalView("form");
    };

    const handleServiceOpen = (serviceId) => {
        const nextId = String(serviceId);
        setActiveTab(TAB_SERVICE);
        setActiveServiceId(nextId);
        setPagination((prev) => ({
            ...prev,
            [TAB_SERVICE]: { ...prev[TAB_SERVICE], page: 1 },
        }));
        fetchFaqsByService(nextId);
        setModalView("list");
        setIsFaqModalOpen(true);
    };

    const handleServiceEdit = (serviceId) => {
        navigate("/services/create", { state: { id: serviceId } });
    };

    const handleServiceDelete = async (serviceId) => {
        if (!window.confirm("Delete this service?")) return;

        const deleted = await callDeleteWithFallback([
            `services/${serviceId}`,
            `/services/${serviceId}`,
        ]);

        if (!deleted) {
            toast.error("Failed to delete service");
            return;
        }

        toast.success("Service deleted successfully");
        if (String(activeServiceId) === String(serviceId)) {
            closeModal();
            setActiveServiceId("");
        }
        fetchServices();
        setFaqs([]);
    };

    const handleDeleteFaq = async (faqId) => {
        if (!faqId) return;
        if (!window.confirm("Delete this FAQ?")) return;

        const result = await apiCall({
            endpoint: `/faqs/${faqId}`,
            method: "DELETE",
        });

        if (!isSuccessResponse(result)) {
            toast.error("Failed to delete FAQ");
            return;
        }

        toast.success("FAQ deleted successfully");
        if (String(form.id || "") === String(faqId)) {
            setForm(getEmptyForm(activeServiceId));
            setModalView("list");
        }
        fetchFaqsByService(activeServiceId);
        fetchServices();
    };

    const renderForm = (onCancel) => (
        <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-800">FAQ Details</h3>
                <button
                    type="button"
                    onClick={() => {
                        onCancel();
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                >
                    Cancel
                </button>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Service</label>
                <select
                    name="service_id"
                    value={form.service_id}
                    onChange={(e) => setForm({ ...form, service_id: e.target.value, category_id: "" })}
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    required
                >
                    <option value="">-- Select Service --</option>
                    {services.map((srv) => (
                        <option key={srv.id} value={srv.id}>
                            {srv.name}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">FAQ Question</label>
                <input
                    type="text"
                    name="question"
                    value={form.question}
                    onChange={handleChange}
                    placeholder="Enter FAQ Question"
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    required
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">FAQ Answer</label>
                <textarea
                    name="answer"
                    value={form.answer}
                    onChange={handleChange}
                    placeholder="Enter FAQ Answer"
                    className="w-full border border-gray-300 p-2 rounded text-sm"
                    rows="3"
                    required
                />
            </div>

            <div>
                <button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm transition"
                >
                    {form.id ? "Update FAQ" : "Add FAQ"}
                </button>
            </div>
        </form>
    );

    const handleFormCancel = () => {
        if (isFaqModalOpen) {
            setModalView("list");
        }
        setForm(getEmptyForm(activeServiceId));
    };

    const closeModal = () => {
        setIsFaqModalOpen(false);
        setModalView("list");
        setForm(getEmptyForm(activeServiceId));
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Toaster position="top-center" />
            <div className="px-6 py-5 bg-white min-h-screen">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <h2 className="text-lg font-bold">FAQ Management</h2>
                    <button
                        type="button"
                        onClick={handleAdd}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm transition"
                    >
                        Add FAQ
                    </button>
                </div>

                <div className="border rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-700">Services</h4>
                        <span className="text-xs text-gray-500">{totalServiceRecords} total</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-white border-b">
                                <tr>
                                    <th className="text-left px-3 py-2">Sr No</th>
                                    <th className="text-left px-3 py-2">Service</th>
                                    <th className="text-left px-3 py-2">FAQs</th>
                                    <th className="text-left px-3 py-2">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentServices.length > 0 ? (
                                    currentServices.map((srv, i) => {
                                        const count = Number(srv.faq_count || 0);
                                        return (
                                            <tr
                                                key={srv.id}
                                                className="border-t hover:bg-gray-50 cursor-pointer"
                                                onClick={() => handleServiceOpen(srv.id)}
                                            >
                                                <td className="px-3 py-2">{servicesFirstIndex + i + 1}</td>
                                                <td className="px-3 py-2 font-medium text-gray-800">{srv.name}</td>
                                                <td className="px-3 py-2">
                                                    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded border border-blue-200 bg-blue-50 text-blue-700">
                                                        {count}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleServiceOpen(srv.id);
                                                            }}
                                                            className="text-blue-600 text-xs font-medium"
                                                        >
                                                            Open
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleServiceEdit(srv.id);
                                                            }}
                                                            className="text-green-600 text-xs font-medium"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleServiceDelete(srv.id);
                                                            }}
                                                            className="text-red-600 text-xs font-medium"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="text-center py-4 text-gray-500">
                                            No services available.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="border-t">
                        <Pagination
                            currentPage={servicesPage}
                            totalPages={totalServicePages}
                            onPageChange={setServicesPage}
                            recordsPerPage={servicesPerPage}
                            onRecordsPerPageChange={(value) => {
                                setServicesPage(1);
                                setServicesPerPage(value);
                            }}
                            totalRecords={totalServiceRecords}
                            count={displayedServicesCount}
                        showFirstLastControls
                        compact
                        />
                    </div>
                </div>
            </div>
            {isFaqModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
                    onClick={closeModal}
                >
                    <div
                        className="bg-white rounded-lg shadow-lg w-[95%] max-w-4xl max-h-[85vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-4 py-3 border-b flex items-center justify-between">
                            <div>
                                <h3 className="text-base font-semibold text-gray-800">
                                    FAQs - {activeService?.name || "Service"}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {totalRecords} FAQ(s) found
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                {modalView === "form" && (
                                    <button
                                        type="button"
                                        onClick={() => setModalView("list")}
                                        className="text-sm text-gray-500 hover:text-gray-700"
                                    >
                                        Back to List
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="text-gray-500 hover:text-gray-700 text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>

                        <div className="p-4 overflow-auto max-h-[70vh]">
                            {modalView === "form" ? (
                                renderForm(handleFormCancel)
                            ) : (
                                <>
                                    <div className="border rounded overflow-x-auto">
                                        <table className="min-w-full text-xs">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="text-left px-3 py-2">Sr No</th>
                                                    <th className="text-left px-3 py-2">Question</th>
                                                    <th className="text-left px-3 py-2">Answer</th>
                                                    <th className="text-left px-3 py-2">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentFaqs.map((faq, i) => (
                                                    <tr key={faq.id} className="border-t">
                                                        <td className="px-3 py-2">{indexOfFirstRecord + i + 1}</td>
                                                        <td className="px-3 py-2">{faq.question}</td>
                                                        <td className="px-3 py-2">{faq.answer}</td>
                                                        <td className="px-3 py-2">
                                                            <div className="flex items-center gap-3">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        handleEdit(faq);
                                                                        setModalView("form");
                                                                    }}
                                                                    className="text-blue-600 text-xs"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteFaq(faq.id)}
                                                                    className="text-red-600 text-xs"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {currentFaqs.length === 0 && (
                                                    <tr>
                                                        <td colSpan={4} className="text-center py-4 text-gray-500">
                                                            No FAQs found for this service.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-4">
                                        <Pagination
                                            currentPage={activePagination.page}
                                            totalPages={totalPages}
                                            onPageChange={(page) =>
                                                setPagination((prev) => ({
                                                    ...prev,
                                                    [activeTab]: { ...prev[activeTab], page },
                                                }))
                                            }
                                            recordsPerPage={activePagination.perPage}
                                            onRecordsPerPageChange={(value) =>
                                                setPagination((prev) => ({
                                                    ...prev,
                                                    [activeTab]: { page: 1, perPage: value },
                                                }))
                                            }
                                            totalRecords={totalRecords}
                                            count={displayedFaqsCount}
                                        showFirstLastControls
                                        compact
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default Faqs;
